package org.dronda.app.web.ui.util

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.State
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import kotlinx.browser.window
import kotlinx.coroutines.DelicateCoroutinesApi
import kotlinx.coroutines.FlowPreview
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.channels.BufferOverflow
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.launch
import org.w3c.dom.events.Event
import kotlin.time.Duration.Companion.milliseconds

@OptIn(DelicateCoroutinesApi::class, FlowPreview::class)
private object WindowSizeProducer {
    private val state = MutableSharedFlow<Unit>(
        extraBufferCapacity = 1,
        onBufferOverflow = BufferOverflow.DROP_OLDEST
    )
    val internalListener: (Event) -> Unit = {
        state.tryEmit(Unit)
    }

    init {
        GlobalScope.launch {
            state
                .debounce(16.milliseconds)
                .collect {
                    val size = getWindowSize()
                    listeners.forEach { listener -> listener(size) }
                }
        }
    }

    val listeners = mutableListOf<(WindowSize) -> Unit>()
    fun addListener(listener: (WindowSize) -> Unit) {
        listeners.add(listener)
        if (listeners.size == 1) {
            window.addEventListener("resize", internalListener)
        }
    }
    fun removeListener(listener: (WindowSize) -> Unit) {
        listeners.remove(listener)

        if (listeners.isEmpty()) {
            window.removeEventListener("resize", internalListener)
        }
    }
}

@Composable
fun rememberWindowSize(): State<WindowSize> {
    val state = remember { mutableStateOf(getWindowSize()) }
    DisposableEffect(Unit) {
        val listener: (WindowSize) -> Unit = {
            state.value = it
        }
        WindowSizeProducer.addListener(listener)

        onDispose {
            WindowSizeProducer.removeListener(listener)
        }
    }

    return state
}

private fun getWindowSize(): WindowSize {
    return WindowSize(
        window.innerWidth,
        window.innerHeight,
    )
}

data class WindowSize(val width: Int, val height: Int)